

import { Webhook, Globe, ExternalLink } from 'lucide-react';
import { QuickAction } from '../../quick-actions';
import { cn } from '@/src/lib/shadcn/cn';
import { useRouter } from 'next/navigation';

export const integrations: QuickAction[] = [
    {
        icon: <Webhook className="w-4 h-4" />,
        text: "Integrations & Webhooks",
        description: "Seamlessly integrate third party scripts, APIs, and webhooks for real-time data.",
        href: "/dashboard/integrations",
        newTabHref: "/dashboard/integrations"
    },
    {
        icon: <Globe className="w-4 h-4" />,
        text: "Domain Management",
        description: "Add and edit all your landing pages and tracking domains in one place.",
        section: "integrations",
        href: "/dashboard/domains",
        newTabHref: "/dashboard/domains"
    }
];

interface IntegrationsProps {
    searchQuery: string;
    onClose: () => void;
}

const filterIntegrations = (items: QuickAction[], query: string) => {
    if (!query) return items;
    const lowerQuery = query.toLowerCase();
    return items.filter(item =>
        item.text?.toLowerCase().includes(lowerQuery) ||
        item.description?.toLowerCase().includes(lowerQuery)
    );
};

export default function Integrations({ searchQuery, onClose }: IntegrationsProps) {
    const router = useRouter();
    const filteredIntegrations = filterIntegrations(integrations, searchQuery);

    if (!filteredIntegrations.length) {
        return null;
    }

    const handleIntegrationClick = (option: QuickAction) => {
        if (option.disabled) return;

        if (option.href) {
            router.push(option.href);
            onClose();
            return;
        }
        onClose();
    };

    return (
        <div>
            <div className="flex items-center gap-3">
                <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">
                    Integrations
                </h3>
                <div className="h-[1px] flex-1 bg-gradient-to-r from-gray-200 to-transparent dark:from-gray-700" />
            </div>
            <div className="mt-3 grid grid-cols-2 gap-2">
                {filteredIntegrations.map((option, index) => (
                    <button
                        key={index}
                        style={{ animationDelay: `${0.2 + index * 0.1}s` }}
                        className={cn(
                            "flex items-start gap-3 w-full text-left px-3 py-2 rounded-lg group",
                            "hover:bg-gray-100 dark:hover:bg-gray-700/50",
                            "text-gray-700 dark:text-gray-300 text-sm",
                            "animate-in fade-in slide-in-from-bottom-2 duration-200 fill-mode-backwards",
                            option.disabled && "opacity-50 cursor-not-allowed hover:bg-transparent dark:hover:bg-transparent"
                        )}
                        data-testid={`integration-item-${option.text ? option.text.replace(/\s+/g, '-').toLowerCase() : index}`}
                        onClick={() => handleIntegrationClick(option)}
                        disabled={option.disabled}
                    >
                        <span className="mt-0.5 text-gray-500 dark:text-gray-400">{option.icon}</span>
                        <div className="flex-1">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-2">
                                    {option.text}
                                    {option.badge && (
                                        <span className="px-1.5 py-0.5 text-[10px] font-medium bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300 rounded-full">
                                            {option.badge}
                                        </span>
                                    )}
                                </div>
                                {option.newTabHref && !option.disabled && (
                                    <span
                                        role="button"
                                        tabIndex={0}
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            window.open(option.newTabHref, '_blank');
                                            onClose();
                                        }}
                                        onKeyDown={(e) => {
                                            if (e.key === 'Enter' || e.key === ' ') {
                                                e.preventDefault();
                                                e.stopPropagation();
                                                window.open(option.newTabHref, '_blank');
                                                onClose();
                                            }
                                        }}
                                        aria-label={`Open ${option.text ?? 'integration'} in new tab`}
                                        className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-gray-200 dark:hover:bg-gray-600 rounded"
                                    >
                                        <ExternalLink className="w-3 h-3 text-gray-500" />
                                    </span>
                                )}
                            </div>
                            <span className="text-xs text-gray-500 dark:text-gray-400">{option.description}</span>
                        </div>
                    </button>
                ))}
            </div>
        </div>
    );
} 